/**
 * Station data-capture tabs — Rate Card, Schedule, Profile, Ad Log, and the
 * Overview-tab Onboarding progress widget. Extracted to a single file so the
 * main StationDash.jsx stays readable.
 *
 * Each tab is a self-contained, network-aware component that:
 *   - loads its own data on mount
 *   - shows an inline {certMsg-style} success / error banner
 *   - never uses window.alert()
 *
 * All four tabs assume the parent passes `station`, `live`, and `setTab`.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;

const DEFAULT_BELTS = [
  { belt_name: "early_morning", belt_label: "Early Morning (4am–6am)",   start_time: "04:00", end_time: "06:00", days_of_week: [1,2,3,4,5,6,7], rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "morning_news",  belt_label: "Morning News (6am–6:30am)", start_time: "06:00", end_time: "06:30", days_of_week: [1,2,3,4,5],     rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "morning_drive", belt_label: "Morning Drive (6:30am–9am)",start_time: "06:30", end_time: "09:00", days_of_week: [1,2,3,4,5],     rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "mid_morning",   belt_label: "Mid Morning (9am–12pm)",    start_time: "09:00", end_time: "12:00", days_of_week: [1,2,3,4,5],     rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "midday",        belt_label: "Midday (12pm–2pm)",         start_time: "12:00", end_time: "14:00", days_of_week: [1,2,3,4,5,6,7], rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "afternoon",     belt_label: "Afternoon (2pm–5pm)",       start_time: "14:00", end_time: "17:00", days_of_week: [1,2,3,4,5,6,7], rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "evening_drive", belt_label: "Evening Drive (5pm–7pm)",   start_time: "17:00", end_time: "19:00", days_of_week: [1,2,3,4,5],     rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "evening",       belt_label: "Evening (7pm–10pm)",        start_time: "19:00", end_time: "22:00", days_of_week: [1,2,3,4,5,6,7], rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
  { belt_name: "night",         belt_label: "Night (10pm–4am)",          start_time: "22:00", end_time: "04:00", days_of_week: [1,2,3,4,5,6,7], rate_30s_ghs: 0, rate_60s_ghs: 0, rate_10s_ghs: 0 },
];

const GH_REGIONS = [
  ["GH-AA","Greater Accra"], ["GH-AH","Ashanti"],   ["GH-WR","Western"],
  ["GH-CP","Central"],       ["GH-EP","Eastern"],   ["GH-TV","Volta"],
  ["GH-NP","Northern"],      ["GH-UE","Upper East"],["GH-UW","Upper West"],
  ["GH-OT","Oti"],           ["GH-BO","Bono"],      ["GH-BE","Bono East"],
  ["GH-AF","Ahafo"],         ["GH-SV","Savannah"],  ["GH-NE","North East"],
  ["GH-WN","Western North"],
];
const REGION_POP = {
  "GH-AA":5400000,"GH-AH":5800000,"GH-WR":2400000,"GH-CP":2900000,
  "GH-EP":2900000,"GH-TV":1900000,"GH-NP":1900000,"GH-UE":1200000,
  "GH-UW":900000,"GH-OT":700000,"GH-BO":1200000,"GH-BE":700000,
  "GH-AF":600000,"GH-SV":600000,"GH-NE":500000,"GH-WN":800000,
};

const DAY_LABELS = ["M","T","W","T","F","S","S"];

// ─── Shared inline message bar (matches certMsg pattern from AdVerify) ───
function MsgBar({ msg, onClose }) {
  if (!msg) return null;
  const ok = msg.type === "success";
  return (
    <div data-testid="msg-bar" style={{
      marginBottom: 12, padding: "10px 14px", borderRadius: 8,
      background: ok ? "#10B98118" : "#EF444418",
      border: `1px solid ${ok ? "#10B98155" : "#EF444455"}`,
      color: ok ? "#10B981" : "#EF4444", fontSize: 13,
      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{ok ? "✓ " : "⚠ "}{msg.text}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit",
        cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Onboarding progress widget — renders in StationDash Overview tab
// ═════════════════════════════════════════════════════════════════════════
export function OnboardingWidget({ onboarding, setTab }) {
  if (!onboarding || onboarding.completion_pct >= 100) return null;
  return (
    <div data-testid="onboarding-progress"
      style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 22px", marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>Station setup</div>
          <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
            {onboarding.completion_pct}% complete · Finish setup to maximise your visibility to agencies
          </div>
        </div>
        <div style={{ color: C.cyan, fontSize: 18, fontWeight: 800, fontFamily: "monospace" }}>
          {onboarding.completion_pct}%
        </div>
      </div>
      <div style={{ height: 4, background: C.border, borderRadius: 2, marginBottom: 14 }}>
        <div style={{ width: `${onboarding.completion_pct}%`, height: "100%",
          background: `linear-gradient(90deg, ${C.cyan}, #10B981)`, borderRadius: 2,
          transition: "width 0.6s ease" }} />
      </div>
      {onboarding.steps.map((step) => (
        <div key={step.id} data-testid={`onboarding-step-${step.id}`}
          style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8,
            opacity: step.done ? 0.6 : 1 }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
            background: step.done ? "#10B981" : C.border,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, color: step.done ? "#fff" : C.muted, fontWeight: 700, marginTop: 1 }}>
            {step.done ? "✓" : ""}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: step.done ? C.muted : C.text, fontSize: 12,
              fontWeight: step.done ? 400 : 600, textDecoration: step.done ? "line-through" : "none" }}>
              {step.label}
            </div>
            {!step.done && step.impact && (
              <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{step.impact}</div>
            )}
          </div>
          {!step.done && step.tab && (
            <button onClick={() => setTab(step.tab)} data-testid={`onboarding-cta-${step.id}`}
              style={{ padding: "4px 12px", background: C.cyanDim, border: `1px solid ${C.cyan}33`,
                color: C.cyan, borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700,
                flexShrink: 0, whiteSpace: "nowrap" }}>
              Do this →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// 1. Rate Card tab
// ═════════════════════════════════════════════════════════════════════════
export function RateCardTab({ station, live, onSaved }) {
  const [rows, setRows] = useState(DEFAULT_BELTS);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get(`/stations/${station.id}/rate-card`).then((r) => {
      if (r.data.rate_card?.length > 0) {
        setRows(DEFAULT_BELTS.map((def) => {
          const f = r.data.rate_card.find((x) => x.belt_name === def.belt_name);
          return f ? { ...def, ...f } : def;
        }));
      }
    }).catch(() => {});
  }, [station.id]);

  const updateCell = (idx, field, value) =>
    setRows((rs) => rs.map((r, i) => i === idx ? { ...r, [field]: Number(value) || 0 } : r));

  const save = async () => {
    setLoading(true); setMsg(null);
    try {
      const filtered = rows.filter((r) => r.rate_30s_ghs > 0);
      const { data } = await api.post(`/stations/${station.id}/rate-card`, { rows: filtered });
      setMsg({ type: "success", text: data.message + " Planning Tool updated immediately." });
      onSaved?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Save failed" });
    } finally {
      setLoading(false);
    }
  };

  const morningDrive = rows.find((r) => r.belt_name === "morning_drive");
  const livePeople = live?.estimated_total_people || station.base_listeners * 8 || 0;
  const cpta = morningDrive?.rate_30s_ghs > 0 && livePeople > 0
    ? (morningDrive.rate_30s_ghs * 1000 / livePeople).toFixed(2) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ background: C.cyanDim, border: `1px solid ${C.cyan}33`, borderRadius: 10, padding: "14px 18px", color: C.text, fontSize: 13, lineHeight: 1.6 }}>
        <b style={{ color: C.cyan }}>Why this matters:</b> Your rate card powers the CPTA (Cost Per Thousand Target Audience)
        figure agencies see when comparing stations in the Planning Tool. Without your real rates this figure is estimated.
        Submit your actual card so agencies see the correct value for your airtime.
      </div>

      <MsgBar msg={msg} onClose={() => setMsg(null)} />

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: T.navy, color: "#fff" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>Airtime Belt</th>
              <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>Time</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>:30 (GH₵)</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>:60 (GH₵)</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>:10 (GH₵)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.belt_name} data-testid={`rate-row-${row.belt_name}`}
                style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: "10px 12px", color: C.text }}>{row.belt_label}</td>
                <td style={{ padding: "10px 12px", color: C.muted, fontFamily: "monospace", fontSize: 12 }}>
                  {row.start_time}–{row.end_time}
                </td>
                {["rate_30s_ghs", "rate_60s_ghs", "rate_10s_ghs"].map((f) => (
                  <td key={f} style={{ padding: "6px 8px" }}>
                    <input type="number" min={0} step={50} value={row[f] || ""}
                      data-testid={`rate-${row.belt_name}-${f}`}
                      onChange={(e) => updateCell(i, f, e.target.value)}
                      style={{ width: "100%", padding: "7px 10px", background: C.surface,
                        border: `1px solid ${C.border}`, borderRadius: 6, color: C.text,
                        textAlign: "right", fontFamily: "monospace", fontSize: 13 }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cpta && (
        <div data-testid="cpta-preview" style={{ background: C.surface, borderLeft: `3px solid ${C.cyan}`, borderRadius: 8, padding: "12px 16px", color: C.text, fontSize: 13 }}>
          At these rates, your <b>morning drive</b> costs GH₵ {morningDrive.rate_30s_ghs.toLocaleString()} per :30 spot.
          With {livePeople.toLocaleString()} estimated listeners right now, your <b>CPTA = GH₵ {cpta}</b> per 1,000 listeners.
        </div>
      )}

      <div>
        <button data-testid="rate-card-save" onClick={save} disabled={loading}
          style={{ padding: "11px 24px", background: C.cyan, color: C.bg, border: "none",
            borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
            opacity: loading ? 0.5 : 1 }}>
          {loading ? "Saving..." : "Save rate card →"}
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// 2. Schedule tab — list + add form
// ═════════════════════════════════════════════════════════════════════════
function beltFromTime(t) {
  const [h, m] = (t || "00:00").split(":").map(Number);
  const total = h * 60 + (m || 0);
  if (total >= 240 && total < 360)  return "early_morning";
  if (total >= 360 && total < 390)  return "morning_news";
  if (total >= 390 && total < 540)  return "morning_drive";
  if (total >= 540 && total < 720)  return "mid_morning";
  if (total >= 720 && total < 840)  return "midday";
  if (total >= 840 && total < 1020) return "afternoon";
  if (total >= 1020 && total < 1140)return "evening_drive";
  if (total >= 1140 && total < 1320)return "evening";
  return "night";
}
function daysLabel(days) {
  if (!days || !days.length) return "—";
  if (days.length === 7) return "Daily";
  if (days.length === 5 && days.every((d) => d >= 1 && d <= 5)) return "Mon–Fri";
  if (days.length === 2 && days.includes(6) && days.includes(7)) return "Weekends";
  return days.map((d) => DAY_LABELS[d - 1] || "?").join("");
}

export function ScheduleTab({ station, onSaved }) {
  const [progs, setProgs] = useState([]);
  const [form, setForm] = useState({
    name: "", host: "", genre: "Talk", language: "English",
    start_time: "06:30", end_time: "09:00",
    days_of_week: [1, 2, 3, 4, 5],
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => api.get(`/stations/${station.id}/programmes`)
    .then((r) => setProgs(r.data.programmes || [])).catch(() => {});
  useEffect(() => { load(); }, [station.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDay = (d) => setForm((f) => ({
    ...f,
    days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d].sort(),
  }));

  const add = async () => {
    if (!form.name.trim()) { setMsg({ type: "error", text: "Show name is required" }); return; }
    setLoading(true); setMsg(null);
    try {
      await api.post(`/stations/${station.id}/programmes`, {
        ...form, belt_name: beltFromTime(form.start_time),
      });
      setMsg({ type: "success", text: `"${form.name}" added to schedule.` });
      setForm((f) => ({ ...f, name: "", host: "" }));
      load();
      onSaved?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed to add programme" });
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/stations/${station.id}/programmes/${id}`);
      load(); onSaved?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Delete failed" });
    }
  };

  const inp = { padding: "8px 10px", background: C.surface, border: `1px solid ${C.border}`,
    color: C.text, borderRadius: 6, fontSize: 13, fontFamily: "inherit" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <MsgBar msg={msg} onClose={() => setMsg(null)} />

      {/* List */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 14 }}>
          Current schedule ({progs.length})
        </div>
        {progs.length === 0 ? (
          <div style={{ padding: 24, color: C.muted, fontSize: 13, textAlign: "center" }}>
            No programmes yet. Add your first show below.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.surface, color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {["Show","Host","Time","Days","Genre","Lang",""].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {progs.map((p) => (
                <tr key={p.id} data-testid={`prog-row-${p.id}`} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: "10px 12px", color: C.text, fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: "10px 12px", color: C.muted }}>{p.host || "—"}</td>
                  <td style={{ padding: "10px 12px", color: C.muted, fontFamily: "monospace" }}>{p.start_time}–{p.end_time}</td>
                  <td style={{ padding: "10px 12px", color: C.muted }}>{daysLabel(p.days_of_week)}</td>
                  <td style={{ padding: "10px 12px", color: C.muted }}>{p.genre}</td>
                  <td style={{ padding: "10px 12px", color: C.muted }}>{p.language}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <button data-testid={`prog-delete-${p.id}`} onClick={() => remove(p.id)}
                      style={{ background: "transparent", border: `1px solid ${C.border}`,
                        color: "#EF4444", borderRadius: 6, padding: "4px 10px", fontSize: 11,
                        cursor: "pointer" }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add form */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Add a programme</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
          <input data-testid="prog-name" placeholder="Show name (e.g. Kokrokoo)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} style={inp} />
          <input data-testid="prog-host" placeholder="Host name (optional)" value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })} style={inp} />
          <select data-testid="prog-genre" value={form.genre}
            onChange={(e) => setForm({ ...form, genre: e.target.value })} style={inp}>
            {["News","Talk","Music","Sports","Gospel","Drama","Current Affairs","Magazine"].map((g) => <option key={g}>{g}</option>)}
          </select>
          <select data-testid="prog-language" value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })} style={inp}>
            {["English","Twi","Ga","Hausa","Ewe","Dagbani","Fante","Other"].map((l) => <option key={l}>{l}</option>)}
          </select>
          <input data-testid="prog-start" type="time" value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })} style={inp} />
          <input data-testid="prog-end" type="time" value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })} style={inp} />
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>DAYS</span>
          {DAY_LABELS.map((l, i) => {
            const d = i + 1;
            const on = form.days_of_week.includes(d);
            return (
              <button key={i} data-testid={`prog-day-${d}`} onClick={() => toggleDay(d)}
                style={{ width: 32, height: 32, borderRadius: "50%",
                  background: on ? C.cyan + "22" : "transparent",
                  border: `1px solid ${on ? C.cyan : C.border}`,
                  color: on ? C.cyan : C.muted, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                {l}
              </button>
            );
          })}
        </div>
        <button data-testid="prog-add" onClick={add} disabled={loading}
          style={{ marginTop: 16, padding: "10px 22px", background: C.cyan, color: C.bg,
            border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
            opacity: loading ? 0.5 : 1 }}>
          {loading ? "Adding..." : "Add programme"}
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// 3. Station Profile tab
// ═════════════════════════════════════════════════════════════════════════
export function ProfileTab({ station, onSaved }) {
  const [form, setForm] = useState({
    logo_url: station.logo_url || "",
    website: station.website || "",
    facebook_url: station.facebook_url || "",
    twitter_handle: station.twitter_handle || "",
    whatsapp_number: station.whatsapp_number || "",
    description: station.description || "",
    transmitter_power_watts: station.transmitter_power_watts || 0,
    coverage_radius_km: station.coverage_radius_km || 0,
    coverage_regions: station.coverage_regions || [],
  });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleRegion = (code) => setForm((f) => ({
    ...f,
    coverage_regions: f.coverage_regions.includes(code)
      ? f.coverage_regions.filter((c) => c !== code)
      : [...f.coverage_regions, code],
  }));

  const estimatedPop = form.coverage_regions.reduce((sum, r) => sum + (REGION_POP[r] || 500000), 0);

  const save = async () => {
    setLoading(true); setMsg(null);
    try {
      const payload = { ...form };
      // Strip empty strings so we don't overwrite with ""
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] === 0) delete payload[k]; });
      if (Object.keys(payload).length === 0) {
        setMsg({ type: "error", text: "Nothing to save" }); setLoading(false); return;
      }
      await api.patch(`/stations/${station.id}/profile`, payload);
      setMsg({ type: "success", text: "Station profile updated." });
      onSaved?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Save failed" });
    } finally { setLoading(false); }
  };

  const inp = { width: "100%", padding: "9px 12px", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, fontSize: 13, fontFamily: "inherit" };
  const label = { display: "block", color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6, textTransform: "uppercase" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <MsgBar msg={msg} onClose={() => setMsg(null)} />

      {/* Public presence */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Public presence</div>

        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={label}>Logo URL</div>
            <input data-testid="profile-logo" value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://..." style={inp} />
          </div>
          {form.logo_url ? (
            <img src={form.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: 8,
              background: C.surface, border: `1px solid ${C.border}`, objectFit: "contain" }}
              onError={(e) => { e.target.style.opacity = 0.2; }} />
          ) : <div style={{ width: 48, height: 48, borderRadius: 8, background: C.surface, border: `1px dashed ${C.border}` }} />}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <div><div style={label}>Website</div>
            <input data-testid="profile-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://www.station.com" style={inp} /></div>
          <div><div style={label}>Facebook page URL</div>
            <input data-testid="profile-fb" value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} placeholder="https://facebook.com/..." style={inp} /></div>
          <div><div style={label}>Twitter / X handle</div>
            <input data-testid="profile-tw" value={form.twitter_handle} onChange={(e) => setForm({ ...form, twitter_handle: e.target.value.replace(/^@/, "") })} placeholder="@stationhandle" style={inp} /></div>
          <div><div style={label}>WhatsApp contact</div>
            <input data-testid="profile-wa" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+233 24 ..." style={inp} /></div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={label}>Short description (max 280 chars)</div>
          <textarea data-testid="profile-desc" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 280) })}
            rows={3} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
          <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{form.description.length}/280</div>
        </div>
      </div>

      {/* Signal coverage */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Signal coverage</div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
          Drives the addressable-population model. The more accurate your coverage, the more accurate your audience estimates.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginBottom: 14 }}>
          <div><div style={label}>Transmitter power (watts)</div>
            <input data-testid="profile-power" type="number" min={100} max={50000} value={form.transmitter_power_watts}
              onChange={(e) => setForm({ ...form, transmitter_power_watts: Number(e.target.value) || 0 })} style={inp} /></div>
          <div><div style={label}>Coverage radius (km)</div>
            <input data-testid="profile-radius" type="number" min={1} max={200} value={form.coverage_radius_km}
              onChange={(e) => setForm({ ...form, coverage_radius_km: Number(e.target.value) || 0 })} style={inp} /></div>
        </div>

        <div style={label}>Coverage regions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {GH_REGIONS.map(([code, name]) => {
            const on = form.coverage_regions.includes(code);
            return (
              <button key={code} data-testid={`profile-region-${code}`} onClick={() => toggleRegion(code)}
                style={{ padding: "6px 12px", borderRadius: 16, fontSize: 11, cursor: "pointer",
                  background: on ? C.cyan + "22" : "transparent",
                  border: `1px solid ${on ? C.cyan : C.border}`,
                  color: on ? C.cyan : C.muted, fontWeight: on ? 700 : 500 }}>
                {code} — {name}
              </button>
            );
          })}
        </div>

        {form.coverage_regions.length > 0 && (
          <div data-testid="profile-pop-estimate" style={{ marginTop: 14, padding: "10px 14px",
            background: C.cyanDim, borderRadius: 8, color: C.text, fontSize: 12 }}>
            With these regions, WaveScorePro will model your addressable audience as{" "}
            <b style={{ color: C.cyan }}>{estimatedPop.toLocaleString()} people</b>.
          </div>
        )}
      </div>

      <div>
        <button data-testid="profile-save" onClick={save} disabled={loading}
          style={{ padding: "11px 24px", background: C.cyan, color: C.bg, border: "none",
            borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
            opacity: loading ? 0.5 : 1 }}>
          {loading ? "Saving..." : "Save profile →"}
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// 4. Ad Log CSV upload tab
// ═════════════════════════════════════════════════════════════════════════
const SAMPLE_CSV = `date,time,ad_title,client_name,agency_name,duration_seconds
2026-06-22,07:15,Guinness Smooth :30,Guinness Ghana,Media General,30
2026-06-22,07:45,MTN MoMo Summer,MTN Ghana,Interpublic,30
2026-06-22,12:30,Stanbic Loan Promo,Stanbic Bank,Origin8,30`;

export function AdLogTab({ station, onSaved }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wavescorepro_ad_log_template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { setMsg({ type: "error", text: "Choose a CSV file first" }); return; }
    setLoading(true); setMsg(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/stations/${station.id}/ad-log/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setMsg({ type: data.certificates_created > 0 ? "success" : "error",
        text: `${data.certificates_created} certificate${data.certificates_created !== 1 ? "s" : ""} created · ${data.error_count} error${data.error_count !== 1 ? "s" : ""}` });
      onSaved?.();
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Upload failed" });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <MsgBar msg={msg} onClose={() => setMsg(null)} />

      {/* Drop zone */}
      <div style={{ background: C.card, border: `2px dashed ${C.border}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>📤</div>
        <div style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
          Drop your ad log CSV here or click to browse
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>
          Required columns: date, time, ad_title, client_name<br />
          Optional: agency_name, duration_seconds
        </div>
        <label data-testid="adlog-file-label" style={{ display: "inline-block", padding: "9px 22px", background: C.surface, border: `1px solid ${C.cyan}55`, color: C.cyan, borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
          {file ? `Selected: ${file.name}` : "Choose CSV file"}
          <input data-testid="adlog-file-input" type="file" accept=".csv,text/csv" style={{ display: "none" }}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        </label>
        <div style={{ marginTop: 12 }}>
          <button data-testid="adlog-template" onClick={downloadTemplate}
            style={{ background: "transparent", border: "none", color: C.muted, fontSize: 12,
              cursor: "pointer", textDecoration: "underline" }}>
            Download blank template
          </button>
        </div>
        {file && (
          <div style={{ marginTop: 18 }}>
            <button data-testid="adlog-upload" onClick={upload} disabled={loading}
              style={{ padding: "11px 28px", background: C.cyan, color: C.bg, border: "none",
                borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                opacity: loading ? 0.5 : 1 }}>
              {loading ? "Uploading..." : "Upload and generate certificates →"}
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div data-testid="adlog-result" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            {result.message}
          </div>
          {result.certificate_numbers?.length > 0 && (
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>
              First certificates: {result.certificate_numbers.slice(0, 3).map((n) => (
                <a key={n} href={`/verify/${n}`} target="_blank" rel="noreferrer"
                  style={{ color: C.cyan, marginRight: 10, fontFamily: "monospace", fontSize: 11 }}>{n}</a>
              ))}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>
              ⚠ {result.errors.length} error{result.errors.length !== 1 ? "s" : ""}: {result.errors[0]}
              {result.errors.length > 1 && ` (+${result.errors.length - 1} more)`}
            </div>
          )}
        </div>
      )}

      {/* Column guide */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontWeight: 700 }}>
          CSV column guide
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: C.surface, color: C.muted, fontSize: 10, textTransform: "uppercase" }}>
              {["Column","Required","Format","Example"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", letterSpacing: "0.06em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ["date","Yes","YYYY-MM-DD","2026-06-22"],
              ["time","Yes","HH:MM (24hr)","07:15"],
              ["ad_title","Yes","Text","Guinness Smooth :30"],
              ["client_name","Yes","Text","Guinness Ghana"],
              ["agency_name","No","Text","Media General"],
              ["duration_seconds","No","Number (default 30)","30"],
            ].map((row) => (
              <tr key={row[0]} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600, fontFamily: "monospace" }}>{row[0]}</td>
                <td style={{ padding: "8px 12px", color: row[1] === "Yes" ? "#EF4444" : C.muted }}>{row[1]}</td>
                <td style={{ padding: "8px 12px", color: C.muted, fontFamily: "monospace" }}>{row[2]}</td>
                <td style={{ padding: "8px 12px", color: C.muted, fontFamily: "monospace" }}>{row[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, fontStyle: "italic" }}>
        Certificates generated from uploaded logs carry a <b>PANEL</b> confidence label —
        based on station-reported data rather than direct stream monitoring. When the
        upload timestamp matches a real-time snapshot within ±5 min, we automatically
        upgrade to <b>DIRECT</b>. All certificates are independently verifiable at
        wavescorepro.com/verify/[certificate-number].
      </div>
    </div>
  );
}
